<script>
import {
  ConfettiInput,
  ConfettiMessage,
  ConfettiPasswordInput,
} from "@kci/confetti-vue";
import AuthContainer from "@/components/auth/AuthContainer";
import { MIN_PW_LENGTH } from "@/helpers/validations";
import { USER_ROLE_MAP } from "@/helpers/user";
import { addSentryData } from "@/helpers/sentry";
import { login, refreshTokens } from "@/helpers/auth";
import { onRouteFailing } from "@/helpers/router";
import { LOCALE_KEY, MAILS_ACCORDION_KEY } from "@/helpers/local-storage-keys";
import updateCurrentUserLocaleMutation from "@/graphql/mutations/UpdateCurrentUserLocale.gql";
import { SUPPORTED_LANGUAGES } from "@/helpers/locale";
import moment from "moment";
import { bannerMessagesStore } from "@/stores/banner-messages-store";

export const LOGIN_DESTINATION = { name: "home" };
export const LABELER_DESTINATION = { name: "companies" };
export const MESSAGE_REASON_MAP = {
  401: "Please sign in again",
};

export default {
  name: "ViewsAuthLogin",
  components: {
    AuthContainer,
    ConfettiPasswordInput,
    ConfettiInput,
    ConfettiMessage,
  },

  data() {
    return {
      email: "",
      formError: null,
      password: "",
      isLoading: false,
      bannerMessagesStore: bannerMessagesStore(),
    };
  },

  computed: {
    isDisabled() {
      return (
        !this.email || this.password.length < MIN_PW_LENGTH || this.isLoading
      );
    },

    message() {
      return this.$t(MESSAGE_REASON_MAP[this.$route.query.reason]);
    },
  },

  methods: {
    async handleSubmit() {
      this.formError = null;
      this.isLoading = true;

      try {
        await login(this.email, this.password);

        if (this.$currentUser.isLabeler) {
          this.$router.push(LABELER_DESTINATION).catch(onRouteFailing);
        } else {
          this.$router
            .push(this.$route.query.redirectUrl || LOGIN_DESTINATION)
            .catch(onRouteFailing);
        }

        addSentryData({
          data: {
            "user.role": USER_ROLE_MAP[this.$currentUser.role],
            organization: this.$currentUser.organization?.code,
            subsidiary: this.$currentUser.defaultSubsidiary?.code,
          },
          type: "tag",
        });

        await this.initLocale();
      } catch (ex) {
        this.formError = this.$t("Unsuccessful login. Invalid credentials.");
      } finally {
        this.isLoading = false;
      }
    },

    async initLocale() {
      const locale = this.$currentUser.locale;

      localStorage.setItem(LOCALE_KEY, locale);
      localStorage.setItem(MAILS_ACCORDION_KEY, null);

      try {
        await this.$apollo.mutate({
          mutation: updateCurrentUserLocaleMutation,
          variables: { locale },
        });
      } catch (error) {
        this.bannerMessagesStore.handleGraphQLErrors(error);
      }

      const hasLocaleChanged = locale !== this.$i18n.locale;
      const isLocaleSupported = SUPPORTED_LANGUAGES.some(
        ({ id }) => id === locale,
      );

      if (!hasLocaleChanged || !isLocaleSupported) {
        return Promise.resolve();
      }

      try {
        let messages = null;

        if (this.$i18n.fallbackLocale !== locale) {
          const url = encodeURI(`/lang/${locale}/messages.json`);
          const response = await fetch(url);

          messages = await response.json();
        }

        this.$i18n.setLocaleMessage(locale, messages);

        await refreshTokens();
      } catch (error) {
        this.bannerMessagesStore.handleGraphQLErrors(error);
      }

      moment.locale(locale);

      this.$i18n.locale = locale;

      return Promise.resolve();
    },
  },
};
</script>

<template>
  <AuthContainer :title="$t('Sign in')" :error="formError">
    <ConfettiMessage
      v-if="$route.query.reason"
      type="info"
      can-close="false"
      hide-icon="true"
      class="reason-message"
    >
      {{ message }}
    </ConfettiMessage>

    <form @submit.prevent="handleSubmit">
      <ConfettiInput
        v-model.trim="email"
        name="email"
        type="email"
        is-required
        :label="$t('Email')"
      />
      <ConfettiPasswordInput
        v-model="password"
        name="password"
        is-required
        :label="$t('Password')"
      />
      <button class="login__continue" type="submit" :disabled="isDisabled">
        {{ $t("Continue") }}
      </button>
      <div class="forgot-password-link">
        <router-link
          to="/forgot-password"
          class="login__forgot-password"
          :title="$t('Forgot password')"
        >
          {{ $t("Forgot password") }}
        </router-link>
      </div>
    </form>
  </AuthContainer>
</template>

<style lang="scss" scoped>
button {
  inline-size: 100%;
  text-align: center;
}

form {
  margin-block-start: 1.4rem;

  .confetti-input {
    margin-block-end: 1.4rem;

    &:nth-of-type(2) {
      margin-block-end: 2.8rem;
    }
  }
}

.forgot-password-link {
  margin-block-start: 1.4rem;
  text-align: center;
}
</style>
